import React, { useEffect, useMemo, useState } from "react";
import { useForm, FormProvider } from "react-hook-form";
import InputDatePicker from "../../EvaluationNew/components/InputDatePicker";
import { displayDateUS } from "../../../utils/dateUtils";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { CloseIcon, SpinnerIcon, DocumentsIcon } from "../../../Icons";
import DocumentFileDropzone from "./DocumentFileDropzone";
import {
  DEFAULT_DOCUMENT_MAX_BYTES,
  formatMaxFileSizeLabel,
  useDocumentFileUpload,
} from "../hooks/useDocumentFileUpload";
import {
  DOCUMENT_BTN,
  DOCUMENT_FIELD,
  DOCUMENT_MODAL,
  DOCUMENT_THEME,
} from "../documentTheme";
import {
  CREDENTIAL_UPLOAD_ACCEPT,
  CREDENTIAL_UPLOAD_EXTENSIONS,
  CREDENTIAL_UPLOAD_TYPES_LABEL,
  isAllowedUploadExtension,
  isNonPreviewableDocument,
} from "../documentUploadTypes";

const CREDENTIAL_TYPES = [
  "professional_license",
  "cpr",
  "driver_license",
  "car_insurance",
  "oig",
  "liability_insurance",
  "tb_test",
  "social_security_number",
  "chest_x_ray_clearance",
  "other",
];

const buildCredentialSchema = (maxBytes) =>
  z
    .object({
      tenantId: z.coerce.number().positive().optional(),
      employeeId: z.coerce.number().positive().optional(),
      type: z.enum(CREDENTIAL_TYPES, {
        required_error: "Credential type is required",
        invalid_type_error: `Type must be one of: ${CREDENTIAL_TYPES.join(", ")}`,
      }),
      displayName: z
        .string()
        .min(1, "Display name is required")
        .max(255, "Display name must be at most 255 characters"),
      notes: z.string().max(65535).optional().nullable(),
      expireAt: z
        .string()
        .optional()
        .nullable()
        .transform((v) => (v === "" || v == null ? null : v)),
      fileBase64: z
        .string({
          required_error: "Document is required",
          invalid_type_error: "Document is required",
        })
        .min(1, "Document is required")
        .max(
          maxBytes,
          `File must be at most ${formatMaxFileSizeLabel(maxBytes)}`,
        ),
      fileName: z
        .string({
          required_error: "Document is required",
          invalid_type_error: "Document is required",
        })
        .min(1, "Document is required"),
    })
    .refine(
      (data) =>
        isAllowedUploadExtension(data.fileName, CREDENTIAL_UPLOAD_EXTENSIONS),
      {
        message: `Allowed file types: ${CREDENTIAL_UPLOAD_EXTENSIONS.join(", ")}`,
        path: ["fileName"],
      },
    );

const CredentialModal = ({
  isOpen,
  onClose,
  onSave,
  mode,
  initialData,
  getCredential,
  maxFileBytes = DEFAULT_DOCUMENT_MAX_BYTES,
}) => {
  const isViewMode = mode === "view";
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);

  const createCredentialRequest = useMemo(
    () => buildCredentialSchema(maxFileBytes),
    [maxFileBytes],
  );

  const methods = useForm({
    resolver: zodResolver(createCredentialRequest),
    defaultValues: {
      displayName: "",
      notes: "",
      expireAt: "",
      fileBase64: "",
      fileName: "",
      type: "professional_license",
    },
    mode: "onBlur",
  });

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    reset,
    formState: { errors, isSubmitting },
    setError,
    clearErrors,
  } = methods;

  const fileName = watch("fileName");

  useEffect(() => {
    const loadCredentialData = async () => {
      if (!isOpen) return;

      setPreviewUrl(null);

      if ((mode === "edit" || mode === "view") && initialData && getCredential) {
        const targetId = initialData.targetDetailId || initialData.id;
        if (targetId) {
          setIsLoadingData(true);
          try {
            const response = await getCredential(targetId);
            if (response && response.status === 1 && response.result.credential) {
              const cred = response.result.credential;
              reset({
                displayName: cred.displayName || "",
                notes: cred.notes || "",
                expireAt: cred.expireAt ? displayDateUS(cred.expireAt) : "",
                type: cred.type || "professional_license",
                fileBase64: cred.imageS3Url
                  ? "EXISTING_FILE_PLACEHOLDER"
                  : "",
                fileName: cred.imageS3Url ? "Existing Document" : "",
              });
              if (cred.imageS3Url) {
                setPreviewUrl(cred.imageS3Url);
              }
            }
          } catch (error) {
            console.error("Error loading credential data:", error);
          } finally {
            setIsLoadingData(false);
          }
          return;
        }
      }

      if (initialData) {
        let latestDetail = initialData;
        if (initialData.details && initialData.details.length > 0) {
          latestDetail = initialData.details.reduce((prev, current) =>
            new Date(prev.createdAt) > new Date(current.createdAt)
              ? prev
              : current,
          );
        }

        const incomingType =
          latestDetail.formKey ||
          latestDetail.type ||
          initialData.formKey ||
          initialData.type ||
          "professional_license";
        const validType = CREDENTIAL_TYPES.includes(incomingType)
          ? incomingType
          : "other";

        reset({
          displayName: latestDetail.displayName || initialData.name || "",
          notes: mode === "create" ? "" : latestDetail.notes || "",
          expireAt:
            mode === "create"
              ? ""
              : latestDetail.expireAt
                ? displayDateUS(latestDetail.expireAt)
                : "",
          fileBase64:
            mode === "edit" && latestDetail.imageS3Url
              ? "EXISTING_FILE_PLACEHOLDER"
              : "",
          fileName:
            mode === "edit" && latestDetail.imageS3Url
              ? "Existing Document"
              : "",
          type: validType,
        });
        if (mode === "edit" && latestDetail.imageS3Url) {
          setPreviewUrl(latestDetail.imageS3Url);
        }
      } else {
        reset({
          displayName: "",
          notes: "",
          expireAt: "",
          fileBase64: "",
          fileName: "",
          type: "professional_license",
        });
      }
    };

    loadCredentialData();
  }, [isOpen, initialData, mode, reset, getCredential]);

  const { handleFileChange, handleFileDrop, handleDragOver, clearFile, maxBytes } = useDocumentFileUpload({
    maxBytes: maxFileBytes,
    setValue,
    setPreviewUrl,
    setError,
    clearErrors,
  });

  const onSubmit = async (data) => {
    try {
      await onSave(data);
      onClose();
    } catch (error) {
      console.error("Submission error", error);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={DOCUMENT_MODAL.backdrop}
      role="dialog"
      aria-modal="true"
      aria-labelledby="credential-modal-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget && !isSubmitting) onClose?.();
      }}
    >
      <div className={DOCUMENT_MODAL.panel}>
        <div className={DOCUMENT_MODAL.head}>
          <div className="min-w-0">
            <h3 id="credential-modal-title" className={DOCUMENT_MODAL.title}>
              {mode === "create"
                ? "Add New Credential"
                : isViewMode
                  ? "View Credential"
                  : "Edit Credential"}
              {isLoadingData ? (
                <SpinnerIcon className="inline animate-spin ml-2 h-4 w-4 text-indigo-500" />
              ) : null}
            </h3>
            <p className={DOCUMENT_MODAL.subtitle}>
              {isViewMode
                ? "Review credential details (read-only)"
                : "Upload a credential document and optional expiry"}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className={DOCUMENT_MODAL.close}
            aria-label="Close"
          >
            <CloseIcon size={14} />
          </button>
        </div>

        <div className={DOCUMENT_MODAL.body}>
          {previewUrl ? (
            <div className="border border-indigo-100 rounded-xl overflow-hidden bg-indigo-50/40 flex justify-center">
              {isNonPreviewableDocument(previewUrl, fileName) ? (
                <div className="p-4 text-center">
                  <DocumentsIcon className="mx-auto h-10 w-10 text-indigo-500 mb-2" />
                  <p className="text-sm text-slate-600 font-medium">
                    Preview not available for this file type
                  </p>
                  <a
                    href={previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={`text-xs mt-1 block ${DOCUMENT_THEME.button.link}`}
                  >
                    Open file in new tab
                  </a>
                </div>
              ) : (
                <img
                  src={previewUrl}
                  alt="Credential Preview"
                  className="max-h-40 object-contain"
                />
              )}
            </div>
          ) : null}

          <FormProvider {...methods}>
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-3">
              <div>
                <label htmlFor="displayName" className={DOCUMENT_FIELD.label}>
                  Display Name
                </label>
                <input
                  type="text"
                  id="displayName"
                  {...register("displayName")}
                  disabled
                  className={DOCUMENT_FIELD.inputDisabled}
                  placeholder="e.g. State Nursing License"
                />
                {errors.displayName ? (
                  <p className={DOCUMENT_FIELD.error}>
                    {errors.displayName.message}
                  </p>
                ) : null}
              </div>

              <div>
                <InputDatePicker
                  name="expireAt"
                  label="Expiry Date"
                  formatSeparator="/"
                  max="9999-12-31"
                  disabled={isViewMode}
                />
              </div>

              <div>
                <label htmlFor="notes" className={DOCUMENT_FIELD.label}>
                  Notes
                </label>
                <textarea
                  id="notes"
                  rows={3}
                  {...register("notes")}
                  readOnly={isViewMode}
                  className={`${DOCUMENT_FIELD.textarea} ${errors.notes ? "border-red-400" : ""} ${isViewMode ? DOCUMENT_FIELD.inputDisabled : ""}`}
                  placeholder="Additional details..."
                />
                {errors.notes ? (
                  <p className={DOCUMENT_FIELD.error}>{errors.notes.message}</p>
                ) : null}
              </div>

              {!isViewMode ? (
                <div>
                  <label className={DOCUMENT_FIELD.label}>Document</label>
                  <DocumentFileDropzone
                    inputId="credential-file-upload"
                    accept={CREDENTIAL_UPLOAD_ACCEPT}
                    typesLabel={CREDENTIAL_UPLOAD_TYPES_LABEL}
                    maxBytes={maxBytes}
                    fileName={fileName}
                    onFileChange={handleFileChange}
                    onFileDrop={handleFileDrop}
                    onDragOver={handleDragOver}
                    onClear={clearFile}
                    hasError={!!(errors.fileBase64 || errors.fileName)}
                    selectedNamePrefix="File selected: "
                  />
                  {errors.fileName ? (
                    <p className={DOCUMENT_FIELD.error}>
                      {errors.fileName.message}
                    </p>
                  ) : null}
                  {errors.fileBase64 && !errors.fileName ? (
                    <p className={DOCUMENT_FIELD.error}>
                      {errors.fileBase64.message}
                    </p>
                  ) : null}
                </div>
              ) : null}
            </form>
          </FormProvider>
        </div>

        <div className={DOCUMENT_MODAL.footer}>
          <button
            type="button"
            onClick={onClose}
            disabled={isSubmitting}
            className={isViewMode ? DOCUMENT_BTN.modalPrimary : DOCUMENT_BTN.modalCancel}
          >
            {isViewMode ? "Close" : "Cancel"}
          </button>
          {!isViewMode ? (
            <button
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={isSubmitting}
              className={DOCUMENT_BTN.modalPrimary}
            >
              {isSubmitting ? (
                <>
                  <SpinnerIcon className="animate-spin" size={14} />
                  Saving...
                </>
              ) : mode === "create" ? (
                "Create"
              ) : (
                "Save Changes"
              )}
            </button>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default CredentialModal;
